import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const ResellerAgreementPage = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>Reseller Agreement - SELF Labs</title>
        <meta name="description" content="SELF Phone Reseller Agreement - Effective Date: April 1, 2025" />
      </Helmet>

      {/* Background Effects */}
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_center,rgba(138,43,226,0.1)_0%,transparent_100%)]" />
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_center,rgba(138,43,226,0.03)_0%,transparent_1px)] bg-[length:24px_24px]" />

      <div className="relative">
        <div className="container mx-auto px-4 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-8 bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-purple-400 to-blue-400">
              📜 SELF Phone Reseller Agreement
            </h1>
            <p className="text-indigo-400 mb-8">Effective Date: April 1, 2025</p>

            <div className="prose prose-invert max-w-none mb-12">
              <p>This Reseller Agreement ("Agreement") is entered into by and between SELF Labs Inc., with its principal office at 1209 N Orange St, Wilmington, Delaware, ("SELF Labs," "we," "us," or "our") and the approved reseller ("Reseller," "you," or "your").</p>
              <p>By signing or otherwise accepting this Agreement, Reseller agrees to the following terms:</p>
            </div>

            <div className="space-y-12">
              {/* Authorization to Resell */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">1. Authorization to Resell</h2>
                <div className="prose prose-invert max-w-none">
                  <p>SELF Labs grants Reseller a non-exclusive, non-transferable right to market, sell, and distribute the SELF Phone and associated products ("Products") solely under the terms of this Agreement.</p>
                  <p>Reseller must receive prior written approval from SELF Labs for:</p>
                  <ul>
                    <li>Any resale activities</li>
                    <li>Use of SELF Labs' trademarks, logos, and marketing materials</li>
                    <li>Distribution outside approved sales channels (online, physical stores, events)</li>
                  </ul>
                  <p>Unauthorized resale, sub-resale, or sublicensing is strictly prohibited.</p>
                </div>
              </section>

              {/* Reseller Responsibilities */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">2. Reseller Responsibilities</h2>
                <div className="prose prose-invert max-w-none">
                  <p>Reseller agrees to:</p>
                  <ul>
                    <li>Accurately represent the features and benefits of the SELF Phone and associated services.</li>
                    <li>Disclose to customers that the SELF Phone requires acceptance of SELF Labs' Terms and Conditions and Privacy Policy.</li>
                    <li>Maintain brand integrity and product positioning as directed by SELF Labs.</li>
                    <li>Submit any marketing, promotional, or advertising materials referencing SELF Labs products for prior written approval.</li>
                    <li>Protect all customer information in compliance with applicable data privacy laws.</li>
                  </ul>
                </div>
              </section>

              {/* Pricing and Sales */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">3. Pricing and Sales</h2>
                <div className="prose prose-invert max-w-none">
                  <p>Reseller may set its own retail prices unless otherwise specified by SELF Labs (e.g., MSRP guidance for brand consistency).</p>
                  <p>Reseller may not make false claims, exaggerated promises, or unsupported statements about SELF Phone functionality or the Signal Economy.</p>
                </div>
              </section>

              {/* Restrictions */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">4. Restrictions on Modification and Reverse Engineering</h2>
                <div className="prose prose-invert max-w-none">
                  <p>Reseller shall not modify, alter, disassemble, reverse engineer, or tamper with SELF Phone hardware or software.</p>
                  <p>Reseller shall not attempt to remove, replace, or bypass SELF Labs' proprietary software, encryption, or security systems.</p>
                </div>
              </section>

              {/* Customer Support */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">5. Customer Support and Returns</h2>
                <div className="prose prose-invert max-w-none">
                  <p>SELF Labs is responsible for:</p>
                  <ul>
                    <li>Hardware warranty claims</li>
                    <li>Technical support relating to software and platform services</li>
                  </ul>
                  <p>Reseller is responsible for:</p>
                  <ul>
                    <li>First-line customer interactions and inquiries</li>
                    <li>Clearly communicating SELF Labs' official support and warranty policies to customers</li>
                  </ul>
                  <p>Any returns outside of hardware defects must be handled through Reseller's own return policies unless otherwise agreed in writing.</p>
                </div>
              </section>

              {/* Compliance */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">6. Compliance with Laws</h2>
                <div className="prose prose-invert max-w-none">
                  <p>Reseller agrees to comply with all applicable:</p>
                  <ul>
                    <li>Federal, state, local, and international laws</li>
                    <li>Export control and economic sanctions laws</li>
                    <li>Data privacy and protection regulations</li>
                  </ul>
                  <p>Reseller may not sell or distribute SELF Phones to embargoed territories or restricted individuals/entities.</p>
                </div>
              </section>

              {/* Term and Termination */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">7. Term and Termination</h2>
                <div className="prose prose-invert max-w-none">
                  <p>This Agreement shall remain in effect until terminated by either party with thirty (30) days written notice.</p>
                  <p>SELF Labs may immediately terminate the Agreement for cause if:</p>
                  <ul>
                    <li>Reseller violates any term herein</li>
                    <li>Reseller damages brand reputation, engages in unauthorized sales, or breaches compliance obligations</li>
                  </ul>
                  <p>Upon termination:</p>
                  <ul>
                    <li>Reseller must cease all use of SELF Labs intellectual property and branding</li>
                    <li>SELF Labs reserves the right to repurchase unsold inventory at its sole discretion</li>
                  </ul>
                </div>
              </section>

              {/* Liability */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">8. Limitation of Liability</h2>
                <div className="prose prose-invert max-w-none">
                  <p>SELF Labs shall not be liable for any indirect, incidental, consequential, special, or punitive damages resulting from Reseller's activities, sales, or use of the SELF Phone.</p>
                </div>
              </section>

              {/* IP */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">9. Intellectual Property</h2>
                <div className="prose prose-invert max-w-none">
                  <p>All logos, trademarks, software, patents, proprietary technologies (including the Harmony Signal Engine™, Role Model AI™, Aura Shield™, and Signal Economy™ systems) remain the exclusive intellectual property of SELF Labs Inc.</p>
                  <p>Reseller receives no ownership rights, only limited marketing rights under this Agreement.</p>
                </div>
              </section>

              {/* Governing Law */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">10. Governing Law</h2>
                <div className="prose prose-invert max-w-none">
                  <p>This Agreement shall be governed by and construed in accordance with the laws of the State of Delaware, United States.</p>
                  <p>All disputes arising from this Agreement shall be resolved through binding arbitration located in Wilmington, Delaware.</p>
                </div>
              </section>

              {/* Entire Agreement */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">11. Entire Agreement</h2>
                <div className="prose prose-invert max-w-none">
                  <p>This Agreement constitutes the full and complete understanding between SELF Labs and Reseller regarding the resale of SELF Labs products.</p>
                  <p>No oral promises or prior agreements shall have any force or effect unless made in writing and signed by authorized representatives.</p>
                </div>
              </section>
            </div>
          </motion.div>

          {/* Footer */}
          <div className="mt-20 pt-12 border-t border-indigo-500/20">
            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-3 gap-8">
                <div>
                  <h3 className="text-xl font-semibold mb-4 text-indigo-400">SELF Labs Inc.</h3>
                  <p className="text-gray-400">Powered by Role Model AI + Aura Protocol</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-4 text-indigo-400">Legal</h3>
                  <div className="space-y-2">
                    <Link to="/terms" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      Terms & Conditions
                    </Link>
                    <Link to="/privacy" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      Privacy Policy
                    </Link>
                    <Link to="/reseller-agreement" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      Reseller Agreement
                    </Link>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-4 text-indigo-400">Contact</h3>
                  <div className="space-y-2">
                    <a href="mailto:data@rolemodel.ai" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      data@rolemodel.ai
                    </a>
                    <a href="https://x.com/selflabsinc" target="_blank" rel="noopener noreferrer" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      @selflabsinc
                    </a>
                  </div>
                </div>
              </div>
              <div className="mt-12 pt-8 border-t border-indigo-500/20 text-center text-sm text-gray-400">
                <p>&copy; {new Date().getFullYear()} SELF Labs Inc. All rights reserved.</p>
                <p className="mt-2">Protected by PureCipher's OMNISEAL™ encryption suite</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResellerAgreementPage; 